import {
	createFileRoute,
	Link,
	Outlet,
	useMatches,
	useRouterState,
} from "@tanstack/react-router";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { buildArticleJsonLd, JsonLd } from "@/lib/json-ld";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/blog")({
	component: BlogLayout,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/blog" });

		return {
			meta: [
				{
					title:
						"Blog - AgentFramer | MCP-Native Media Generation for AI Agents",
				},
				{
					name: "description",
					content:
						"Guides, engineering notes, and model deep dives for teams building AI agents that generate images, video, and audio through MCP.",
				},
				{ property: "og:title", content: "Blog - AgentFramer" },
				{
					property: "og:description",
					content:
						"Guides and engineering notes on MCP-native media generation for AI agents.",
				},
				{ property: "og:image", content: ogImageUrl("blog") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("blog") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/blog" }),
		};
	},
});

export const blogPosts = [
	{
		slug: "what-is-mcp-media-generation",
		title: "What is MCP and how it changes AI media generation",
		description:
			"Model Context Protocol lets AI agents call image, video, and audio tools directly. Here is what that means for media generation in 2026.",
		date: "2026-04-28",
	},
	{
		slug: "ai-agents-image-generation-guide",
		title: "AI agents that generate images: a practical guide",
		description:
			"How AI agents call image models through MCP, handle async jobs, and stitch generations into a finished asset.",
		date: "2026-04-27",
	},
	{
		slug: "claude-code-image-generation",
		title: "How to generate images from Claude Code via MCP",
		description:
			"Connect Claude Code to AgentFramer in two minutes and let it generate, edit, and store images while you keep coding.",
		date: "2026-04-26",
	},
	{
		slug: "agent-video-generation-workflow",
		title: "How AI agents generate video, end to end",
		description:
			"Prompt to storyboard to final mp4. The exact pattern an agent uses to generate, poll, and assemble video through MCP.",
		date: "2026-04-25",
	},
	{
		slug: "ai-music-generation-for-agents",
		title: "AI music generation for agents: tools and patterns",
		description:
			"What it takes for an AI agent to score a video, generate jingles, or compose a soundtrack. Models, costs, and a working pattern.",
		date: "2026-04-24",
	},
	{
		slug: "mcp-vs-rest-api-media",
		title: "MCP vs REST APIs for AI media generation",
		description:
			"REST APIs work fine for engineers. MCP works better for agents. Where each shines and how to pick for your stack.",
		date: "2026-04-23",
	},
	{
		slug: "cost-of-ai-image-generation",
		title: "What does AI image generation actually cost in 2026?",
		description:
			"Per-image, per-second, per-megapixel. Modern image models compared on real workload pricing, not list pricing.",
		date: "2026-04-22",
	},
	{
		slug: "text-to-video-models-compared",
		title: "Best text-to-video models in 2026, compared",
		description:
			"Sora, Veo, Runway, Kling, Luma. Quality, latency, and cost compared from an agent workflow perspective.",
		date: "2026-04-21",
	},
	{
		slug: "flux-vs-sdxl-comparison",
		title: "FLUX vs Stable Diffusion XL for agent workflows",
		description:
			"Two of the most-used open image models. Where FLUX wins, where SDXL still has the edge, and which to wire into your agent first.",
		date: "2026-04-20",
	},
	{
		slug: "async-image-generation-best-practices",
		title: "Async image generation: why polling beats blocking",
		description:
			"Blocking calls hurt agents. The polling, webhook, and tool-call patterns that keep agents responsive while jobs run.",
		date: "2026-04-18",
	},
	{
		slug: "setup-mcp-cursor-image",
		title: "How to set up MCP image generation in Cursor",
		description:
			"Step-by-step setup to give Cursor image generation through AgentFramer's MCP server. Works with Cursor 0.45 and later.",
		date: "2026-04-17",
	},
	{
		slug: "setup-mcp-claude-desktop",
		title: "How to use MCP image generation in Claude Desktop",
		description:
			"Add AgentFramer to Claude Desktop and let Claude generate images, videos, and audio without leaving the chat.",
		date: "2026-04-16",
	},
	{
		slug: "agent-soundtrack-pipeline",
		title: "Generate soundtracks for video with an AI agent",
		description:
			"A repeatable pattern for an AI agent to score a video clip end to end: extract beats, generate music, mix to length.",
		date: "2026-04-14",
	},
	{
		slug: "enterprise-mcp-media",
		title: "Enterprise MCP: governance, billing, and access control",
		description:
			"What enterprise teams need before they hand an agent the keys to media generation. Workspace controls, budget caps, audit trails.",
		date: "2026-04-12",
	},
	{
		slug: "midjourney-alternative-mcp",
		title: "Midjourney alternative for AI agents: MCP-native generation",
		description:
			"Midjourney is great for humans, painful for agents. The MCP-native alternatives that fit how AI agents actually work.",
		date: "2026-04-10",
	},
	{
		slug: "runway-alternative-agents",
		title: "Runway alternative for AI agents: programmatic video",
		description:
			"Runway is built for editors. Here is how to give an AI agent equivalent video generation power through MCP.",
		date: "2026-04-08",
	},
	{
		slug: "open-source-image-models-2026",
		title: "Best open-source image models for agent workflows in 2026",
		description:
			"FLUX, SDXL, Playground, SD3.5, and friends. Picking the right open-source image model for autonomous agent use.",
		date: "2026-04-05",
	},
	{
		slug: "ai-agent-batch-image-generation",
		title: "Batch image generation for AI agents: patterns and pitfalls",
		description:
			"Generating fifty product shots with one agent call. Concurrency, rate limits, and the failure modes nobody warns you about.",
		date: "2026-04-02",
	},
] as const;

function BlogLayout() {
	const matches = useMatches();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const lastMatch = matches[matches.length - 1];
	const isIndex =
		lastMatch?.fullPath === "/blog" || lastMatch?.fullPath === "/blog/";
	const slug = pathname.replace(/^\/blog\/?/, "").replace(/\/$/, "");
	const post = blogPosts.find((item) => item.slug === slug);

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			{post ? (
				<JsonLd
					data={buildArticleJsonLd({
						path: `/blog/${post.slug}`,
						title: post.title,
						description: post.description,
						datePublished: post.date,
						image: ogImageUrl("blog"),
					})}
				/>
			) : null}
			<PublicHeader />
			<main className="flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="flex flex-col gap-px bg-border">
						<div
							aria-hidden="true"
							className="h-14 rounded-md bg-background sm:h-16"
						/>
						<div className="rounded-md bg-background px-6 py-10 sm:px-8 sm:py-12">
							<div
								className={isIndex ? "mx-auto max-w-5xl" : "mx-auto max-w-2xl"}
							>
								<Outlet />
								{!isIndex && (
									<div className="mt-12">
										<Link
											to="/blog"
											className="text-sm text-muted-foreground hover:text-foreground transition-colors"
										>
											&larr; All articles
										</Link>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
